import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Users, Wifi, Coffee, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/utils/format';
import CarRentalSelector from '@/components/CarRentalSelector';
import AirportTransferSelector from '@/components/AirportTransferSelector';
import MobileMenu from '@/components/MobileMenu';
import Footer from '@/components/Footer';
import TermsModal from '@/components/TermsModal';
import { PaymentMethodLogos } from '@/components/PaymentLogos';

function RoomTypeDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkIn, checkOut, roomType, numGuests } = location.state || {};

  const [bookingForm, setBookingForm] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    guest_country: '',
    num_guests: numGuests || 1,  // Use numGuests from search or default to 1
    quantity: 1,  // Number of rooms of this type
    special_requests: '',
    payment_option: 'flexible',  // 'flexible', 'non_refundable', or 'pay_on_arrival'
    bed_preference: 'double'  // Default to double bed
  });
  const [additionalGuestNames, setAdditionalGuestNames] = useState([]);  // Names for additional rooms
  const [flexibleRatePercentage, setFlexibleRatePercentage] = useState(30);
  const [environmentalFee, setEnvironmentalFee] = useState(5);  // Environmental fee per night per room
  const [paymentSettings, setPaymentSettings] = useState({
    enable_stripe: true,
    enable_nexi: false,
    enable_paypal: false,
    enable_pay_on_arrival: true,
    enable_non_refundable: true
  });
  const [paymentGateway, setPaymentGateway] = useState('stripe'); // 'stripe', 'nexi', or 'paypal'
  const [selectedCar, setSelectedCar] = useState(null);
  const [transferData, setTransferData] = useState({
    required: false,
    type: '', // 'arrival', 'departure', 'round_trip'
    arrival_flight_number: '',
    arrival_flight_time: '',
    departure_flight_number: '',
    departure_flight_time: ''
  });
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [transferPrices, setTransferPrices] = useState({ one_way: 50 });
  // Track pending booking ID to reuse on payment retry instead of creating duplicates
  // Persisted in localStorage to survive Nexi redirect (which destroys React state)
  const [pendingBookingId, setPendingBookingId] = useState(() => {
    const stored = localStorage.getItem('pendingBookingId');
    if (stored) {
      try {
        const { id, expires } = JSON.parse(stored);
        if (Date.now() < expires) return id;
        localStorage.removeItem('pendingBookingId');
      } catch { localStorage.removeItem('pendingBookingId'); }
    }
    return null;
  });
  const isSubmittingRef = React.useRef(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  const savePendingBookingId = (id) => {
    if (id) {
      localStorage.setItem('pendingBookingId', JSON.stringify({
        id,
        expires: Date.now() + 30 * 60 * 1000  // 30 min expiry
      }));
    } else {
      localStorage.removeItem('pendingBookingId');
    }
    setPendingBookingId(id);
  };

  // Fetch payment settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/public/payment-settings`);
        setFlexibleRatePercentage(response.data.flexible_rate_percentage || 30);
        setEnvironmentalFee(response.data.environmental_fee ?? 5);
        const settings = {
          enable_stripe: response.data.enable_stripe ?? true,
          enable_nexi: response.data.enable_nexi ?? false,
          enable_paypal: response.data.enable_paypal ?? false,
          enable_pay_on_arrival: response.data.enable_pay_on_arrival ?? true,
          enable_non_refundable: response.data.enable_non_refundable ?? true,
          flexible_rate_percentage: response.data.flexible_rate_percentage || 30
        };
        
        setPaymentSettings(settings);
        
        // Set default payment gateway based on what's enabled
        if (settings.enable_stripe && !settings.enable_nexi && !settings.enable_paypal) {
          setPaymentGateway('stripe');
        } else if (settings.enable_nexi && !settings.enable_stripe && !settings.enable_paypal) {
          setPaymentGateway('nexi');
        } else if (settings.enable_paypal && !settings.enable_stripe && !settings.enable_nexi) {
          setPaymentGateway('paypal');
        } else if (settings.enable_nexi) {
          setPaymentGateway('nexi');
        } else if (settings.enable_paypal) {
          setPaymentGateway('paypal');
        } else {
          setPaymentGateway('stripe');
        }
      } catch (error) {
        console.error('Failed to fetch payment settings:', error);
      }
    };
    const fetchTransferPrices = async () => {
      try {
        const response = await axios.get(`${API}/settings/transfer-prices`);
        setTransferPrices({
          one_way: response.data.transfer_price_one_way || 50
        });
      } catch (error) {
        console.error('Failed to fetch transfer prices:', error);
      }
    };
    fetchSettings();
    fetchTransferPrices();
  }, []);

  // Set num_guests to 1 for single-occupancy rooms
  useEffect(() => {
    const maxGuestsPerRoom = roomType?.max_guests || roomType?.sample_room?.max_guests || 2;
    if (maxGuestsPerRoom === 1 && bookingForm.num_guests !== 1) {
      setBookingForm(prev => ({ ...prev, num_guests: 1 }));
    }
  }, [roomType, bookingForm.num_guests]);

  if (!roomType || !checkIn || !checkOut) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No room type selected</p>
          <Button onClick={() => navigate('/')}>Return to Home</Button>
        </div>
      </div>
    );
  }

  // Calculate total price based on quantity
  const totalPrice = roomType.total_price * bookingForm.quantity;
  
  // Calculate extra person fee (3rd person surcharge)
  const baseOccupancy = 2;
  const extraPersonFeePerNight = roomType.extra_person_fee || 0;
  const extraPersons = Math.max(0, bookingForm.num_guests - (baseOccupancy * bookingForm.quantity));
  const extraPersonFeeTotal = extraPersons * extraPersonFeePerNight * roomType.nights;
  
  // Room price including extra person fee
  const totalPriceWithExtra = totalPrice + extraPersonFeeTotal;
  
  // Calculate transfer amount from settings
  const transferAmount = transferData.required ? transferPrices.one_way : 0;
  
  // Get non-refundable discount percentage (0% means not offered)
  const nonRefundableDiscount = roomType.non_refundable_discount || 0;
  
  // Calculate discounted room price for non-refundable option
  const discountedRoomPrice = nonRefundableDiscount > 0 
    ? totalPriceWithExtra * (1 - nonRefundableDiscount / 100) 
    : totalPriceWithExtra;
  
  // Calculate final total price (apply discount only to room price if non-refundable)
  // NOTE: Neither transfer amount nor car rental amount is included - both paid separately on arrival
  const finalTotalPrice = bookingForm.payment_option === 'non_refundable'
    ? discountedRoomPrice
    : totalPriceWithExtra;
  
  // Calculate savings for display
  const roomSavings = bookingForm.payment_option === 'non_refundable' && nonRefundableDiscount > 0
    ? totalPriceWithExtra - discountedRoomPrice
    : 0;
  
  // Calculate max guests based on quantity × max_guests per room
  const maxGuestsPerRoom = roomType?.max_guests || roomType?.sample_room?.max_guests || 2;
  const maxGuests = maxGuestsPerRoom * bookingForm.quantity;

  // Handler for quantity change - also manages additional guest names
  const handleQuantityChange = (newQuantity) => {
    const qty = parseInt(newQuantity) || 1;
    setBookingForm({ ...bookingForm, quantity: qty });
    
    // Adjust additional guest names array based on new quantity
    if (qty > 1) {
      const newAdditionalNames = [...additionalGuestNames];
      // Add empty strings for new rooms
      while (newAdditionalNames.length < qty - 1) {
        newAdditionalNames.push('');
      }
      // Remove excess names if quantity decreased
      while (newAdditionalNames.length > qty - 1) {
        newAdditionalNames.pop();
      }
      setAdditionalGuestNames(newAdditionalNames);
    } else {
      setAdditionalGuestNames([]);
    }
  };

  // Handler for updating additional guest names
  const handleAdditionalGuestNameChange = (index, value) => {
    const newNames = [...additionalGuestNames];
    newNames[index] = value;
    setAdditionalGuestNames(newNames);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Guard against concurrent/rapid-fire submissions
    if (isSubmittingRef.current || loading) return;
    isSubmittingRef.current = true;
    setLoading(true);

    try {
      let bookingId = pendingBookingId;
      
      // Only create a new booking if we don't already have a pending one
      if (!bookingId) {
        setLoadingMessage('Creating your reservation...');
        const allGuestNames = [bookingForm.guest_name, ...additionalGuestNames].filter(name => name.trim());
        
        const bookingData = {
          room_type_quantities: {
            [roomType.room_type_id]: bookingForm.quantity
          },
          guest_name: bookingForm.guest_name,
          guest_names_per_room: allGuestNames,
          guest_email: bookingForm.guest_email,
          guest_phone: bookingForm.guest_phone,
          guest_country: bookingForm.guest_country,
          num_guests: bookingForm.num_guests,
          special_requests: bookingForm.special_requests,
          bed_preference: bookingForm.bed_preference,
          check_in: checkIn,
          check_out: checkOut,
          car_id: selectedCar?.car_id || null,
          car_start_date: selectedCar?.car_start_date || null,
          car_end_date: selectedCar?.car_end_date || null,
          transfer_required: transferData.required,
          transfer_type: transferData.required ? transferData.type : null,
          arrival_flight_number: transferData.arrival_flight_number || null,
          arrival_flight_time: transferData.arrival_flight_time || null,
          departure_flight_number: transferData.departure_flight_number || null,
          departure_flight_time: transferData.departure_flight_time || null,
          payment_option: bookingForm.payment_option
        };

        const response = await axios.post(`${API}/bookings/by-room-type`, bookingData);
        bookingId = response.data.id;
        
        // Persist booking ID in localStorage to survive payment redirects
        savePendingBookingId(bookingId);
      }
      
      // If pay on arrival, skip payment and go to confirmation
      if (bookingForm.payment_option === 'pay_on_arrival') {
        savePendingBookingId(null);
        navigate('/booking-success', { 
          state: { 
            bookingId: bookingId,
            paymentOption: 'pay_on_arrival'
          } 
        });
        return;
      }
      
      setLoadingMessage('Redirecting to payment...');
      
      // Handle payment based on selected gateway
      if (paymentGateway === 'nexi') {
        const nexiPaymentResponse = await axios.post(`${API}/bookings/payment/nexi`, {
          booking_id: bookingId,
          payment_type: bookingForm.payment_option === 'non_refundable' ? 'full_payment' : 'deposit',
          customer_email: bookingForm.guest_email,
          customer_name: bookingForm.guest_name
        });

        if (nexiPaymentResponse.data.url) {
          window.location.href = nexiPaymentResponse.data.url;
        } else {
          throw new Error('Payment gateway did not return a redirect URL. Please try again.');
        }
      } else if (paymentGateway === 'paypal') {
        const paypalPaymentResponse = await axios.post(`${API}/bookings/payment/paypal`, {
          booking_id: bookingId,
          payment_type: bookingForm.payment_option === 'non_refundable' ? 'full_payment' : 'deposit',
          origin_url: window.location.origin
        });

        if (paypalPaymentResponse.data.url) {
          window.location.href = paypalPaymentResponse.data.url;
        } else {
          throw new Error('Payment gateway did not return a redirect URL. Please try again.');
        }
      } else {
        const paymentResponse = await axios.post(`${API}/bookings/payment-intent`, {
          booking_id: bookingId,
          origin_url: window.location.origin,
          payment_option: bookingForm.payment_option
        });

        if (paymentResponse.data.checkout_url) {
          window.location.href = paymentResponse.data.checkout_url;
        } else {
          throw new Error('Payment gateway did not return a redirect URL. Please try again.');
        }
      }
    } catch (error) {
      console.error('Booking error:', error);
      
      let errorMessage = 'Failed to create booking';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => {
            const field = err.loc ? err.loc[err.loc.length - 1] : 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail.msg) {
          errorMessage = detail.msg;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Search
          </Button>
        </div>
      </header>

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Room Type Details - Left Column */}
            <div className="lg:col-span-1">
            <Card className="overflow-hidden border-0 shadow-xl">
              <div className="aspect-video bg-gradient-to-br from-blue-100 to-indigo-100 relative">
                {roomType.image_url ? (
                  <img 
                    src={roomType.image_url} 
                    alt={roomType.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <MapPin className="w-24 h-24 text-blue-400" />
                  </div>
                )}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="text-green-600 font-semibold">
                    {roomType.available_count} room{roomType.available_count !== 1 ? 's' : ''} available
                  </span>
                </div>
              </div>
              
              <CardContent className="p-8">
                <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {roomType.name}
                </h1>
                <p className="text-gray-600 text-lg mb-6">{roomType.description}</p>

                {/* Amenities */}
                {roomType.sample_room?.amenities && roomType.sample_room.amenities.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">Amenities</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {roomType.sample_room.amenities.map((amenity, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-gray-700">
                          <Check className="w-5 h-5 text-green-600" />
                          <span>{amenity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Room Info */}
                <div className="border-t pt-6">
                  <div className="flex items-center gap-2 text-gray-700 mb-2">
                    <Users className="w-5 h-5" />
                    <span>Maximum {roomType.max_guests || roomType.sample_room?.max_guests || 2} {(roomType.max_guests || roomType.sample_room?.max_guests || 2) === 1 ? 'guest' : 'guests'}</span>
                  </div>
                </div>

                {/* Pricing */}
                <div className="mt-6 bg-blue-50 rounded-lg p-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700">{formatCurrency(roomType.price_per_night)} × {roomType.nights} night{roomType.nights !== 1 ? 's' : ''}</span>
                    <span className="font-semibold">{formatCurrency(roomType.total_price)}</span>
                  </div>
                  {selectedCar && (
                    <div className="flex justify-between items-center mb-2 text-sm text-amber-700">
                      <span>Car Rental ({selectedCar.car_days} day{selectedCar.car_days !== 1 ? 's' : ''}) - payable on arrival</span>
                      <span className="font-semibold">{formatCurrency(selectedCar.car_amount)}</span>
                    </div>
                  )}
                  <div className="border-t border-blue-200 mt-4 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold">Price per room</span>
                      <span className="text-2xl font-bold text-blue-600">{formatCurrency(roomType.total_price)}</span>
                    </div>
                  </div>
                </div>

                {/* Booking Dates */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 mb-1">Check-in</p>
                      <p className="font-semibold">{formatDate(checkIn)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-1">Check-out</p>
                      <p className="font-semibold">{formatDate(checkOut)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Booking Form - Right Column */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-xl lg:sticky lg:top-4">
              <CardContent className="p-4 sm:p-6 lg:p-8">
                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Complete Your Booking
                </h2>

                <div className="space-y-6">
                  <div>
                    <Label htmlFor="quantity">Number of Rooms *</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      max={roomType.available_count}
                      value={bookingForm.quantity}
                      onChange={(e) => handleQuantityChange(e.target.value)}
                      required
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      {roomType.available_count} room{roomType.available_count !== 1 ? 's' : ''} available
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="num_guests">Number of Guests *</Label>
                    <Input
                      id="num_guests"
                      type="number"
                      min="1"
                      max={maxGuests}
                      value={maxGuestsPerRoom === 1 ? 1 : bookingForm.num_guests}
                      onChange={(e) => setBookingForm({ ...bookingForm, num_guests: parseInt(e.target.value) })}
                      required
                      disabled={maxGuestsPerRoom === 1}
                      className={maxGuestsPerRoom === 1 ? "bg-gray-100" : ""}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      {maxGuestsPerRoom === 1 
                        ? "Single occupancy room - 1 guest only"
                        : `Max ${maxGuests} ${maxGuests === 1 ? 'guest' : 'guests'} (${bookingForm.quantity} room${bookingForm.quantity !== 1 ? 's' : ''} × ${maxGuestsPerRoom} ${maxGuestsPerRoom === 1 ? 'guest' : 'guests'} per room)`
                      }
                    </p>
                    {extraPersonFeePerNight > 0 && bookingForm.num_guests > (baseOccupancy * bookingForm.quantity) && (
                      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid="extra-person-fee-notice">
                        3rd person supplement: {formatCurrency(extraPersonFeePerNight)}/night ({formatCurrency(extraPersonFeeTotal)} total)
                      </p>
                    )}
                  </div>

                  {/* Guest Names Section */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="guest_name">
                        {bookingForm.quantity > 1 ? 'Room 1 - Guest Name *' : 'Full Name *'}
                      </Label>
                      <Input
                        id="guest_name"
                        value={bookingForm.guest_name}
                        onChange={(e) => setBookingForm({ ...bookingForm, guest_name: e.target.value })}
                        required
                        placeholder="John Doe"
                      />
                      {bookingForm.quantity === 1 && (
                        <p className="text-xs text-gray-500 mt-1">Primary guest and contact person</p>
                      )}
                    </div>

                    {/* Additional Guest Names for Multiple Rooms */}
                    {bookingForm.quantity > 1 && (
                      <div className="border-l-4 border-blue-500 pl-4 space-y-3">
                        <p className="text-sm font-medium text-blue-700">
                          Please enter the name for each additional room
                        </p>
                        {additionalGuestNames.map((name, index) => (
                          <div key={index}>
                            <Label htmlFor={`guest_name_${index + 2}`}>
                              Room {index + 2} - Guest Name *
                            </Label>
                            <Input
                              id={`guest_name_${index + 2}`}
                              value={name}
                              onChange={(e) => handleAdditionalGuestNameChange(index, e.target.value)}
                              required
                              placeholder={`Guest name for Room ${index + 2}`}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="guest_email">Email Address *</Label>
                    <Input
                      id="guest_email"
                      type="email"
                      value={bookingForm.guest_email}
                      onChange={(e) => setBookingForm({ ...bookingForm, guest_email: e.target.value })}
                      required
                      placeholder="john.doe@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="guest_phone">Phone Number *</Label>
                    <Input
                      id="guest_phone"
                      type="tel"
                      value={bookingForm.guest_phone}
                      onChange={(e) => setBookingForm({ ...bookingForm, guest_phone: e.target.value })}
                      required
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div>
                    <Label htmlFor="guest_country">Country *</Label>
                    <select
                      id="guest_country"
                      value={bookingForm.guest_country}
                      onChange={(e) => setBookingForm({ ...bookingForm, guest_country: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select your country</option>
                      <option value="Greece">Greece</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="Germany">Germany</option>
                      <option value="France">France</option>
                      <option value="Italy">Italy</option>
                      <option value="Spain">Spain</option>
                      <option value="Netherlands">Netherlands</option>
                      <option value="Belgium">Belgium</option>
                      <option value="Austria">Austria</option>
                      <option value="Switzerland">Switzerland</option>
                      <option value="Poland">Poland</option>
                      <option value="Czech Republic">Czech Republic</option>
                      <option value="Romania">Romania</option>
                      <option value="Bulgaria">Bulgaria</option>
                      <option value="United States">United States</option>
                      <option value="Canada">Canada</option>
                      <option value="Australia">Australia</option>
                      <option value="Russia">Russia</option>
                      <option value="Turkey">Turkey</option>
                      <option value="Israel">Israel</option>
                      <option value="United Arab Emirates">United Arab Emirates</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Bed Preference */}
                  <div>
                    <Label htmlFor="bed_preference" className="text-base font-medium mb-3 block">
                      Bed Configuration *
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div 
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          bookingForm.bed_preference === 'double' 
                            ? 'border-blue-600 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setBookingForm({ ...bookingForm, bed_preference: 'double' })}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="bed_preference"
                            value="double"
                            checked={bookingForm.bed_preference === 'double'}
                            onChange={(e) => setBookingForm({ ...bookingForm, bed_preference: e.target.value })}
                            className="w-4 h-4"
                          />
                          <div>
                            <div className="font-semibold text-gray-900">🛏️ Double Bed</div>
                            <div className="text-sm text-gray-600">One large double bed</div>
                          </div>
                        </div>
                      </div>

                      <div 
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          bookingForm.bed_preference === 'twin' 
                            ? 'border-blue-600 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setBookingForm({ ...bookingForm, bed_preference: 'twin' })}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="bed_preference"
                            value="twin"
                            checked={bookingForm.bed_preference === 'twin'}
                            onChange={(e) => setBookingForm({ ...bookingForm, bed_preference: e.target.value })}
                            className="w-4 h-4"
                          />
                          <div>
                            <div className="font-semibold text-gray-900">🛏️🛏️ Twin Beds</div>
                            <div className="text-sm text-gray-600">Two single beds</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="special_requests">Special Requests (Optional)</Label>
                    <Textarea
                      id="special_requests"
                      value={bookingForm.special_requests}
                      onChange={(e) => setBookingForm({ ...bookingForm, special_requests: e.target.value })}
                      placeholder="e.g., early check-in, ground floor room, extra pillows..."
                      rows={4}
                    />
                  </div>

                  {/* Car Rental Section */}
                  <div className="border-t pt-4">
                    <CarRentalSelector
                      checkIn={checkIn}
                      checkOut={checkOut}
                      onCarSelect={setSelectedCar}
                      selectedCar={selectedCar}
                    />
                  </div>

                  {/* Airport Transfer Section */}
                  <AirportTransferSelector
                    transferData={transferData}
                    onTransferChange={setTransferData}
                  />

                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payment Options & Checkout - Right Column (Third Column) */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-xl lg:sticky lg:top-4">
              <CardContent className="p-4 sm:p-6 lg:p-8">
                <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Payment & Checkout
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Payment Option Section */}
                  <div className="border-t pt-4">
                    <Label className="text-base font-semibold mb-3 block">Payment Option</Label>
                    <div className="space-y-3">
                      {/* Flexible Rate - Show if either Stripe OR Nexi is enabled */}
                      {(paymentSettings.enable_stripe || paymentSettings.enable_nexi || paymentSettings.enable_paypal) && (
                        <div 
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          bookingForm.payment_option === 'flexible' 
                            ? 'border-green-600 bg-green-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setBookingForm({ ...bookingForm, payment_option: 'flexible' })}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            name="payment_option"
                            value="flexible"
                            checked={bookingForm.payment_option === 'flexible'}
                            onChange={(e) => setBookingForm({ ...bookingForm, payment_option: e.target.value })}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-semibold text-gray-900">Flexible Rate</div>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              Secure your reservation with a {formatCurrency(totalPriceWithExtra * (flexibleRatePercentage / 100))} deposit ({flexibleRatePercentage}%).
                              Remaining {formatCurrency(totalPriceWithExtra * (1 - flexibleRatePercentage / 100))} due on arrival.
                            </div>
                          </div>
                        </div>
                      </div>
                      )}

                      {/* Non-Refundable Rate - Only show if discount > 0%, enabled, and a payment gateway is available */}
                      {paymentSettings.enable_non_refundable && nonRefundableDiscount > 0 && (paymentSettings.enable_stripe || paymentSettings.enable_nexi) && (
                        <div 
                          className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                            bookingForm.payment_option === 'non_refundable' 
                              ? 'border-blue-600 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setBookingForm({ ...bookingForm, payment_option: 'non_refundable' })}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="radio"
                              name="payment_option"
                              value="non_refundable"
                              checked={bookingForm.payment_option === 'non_refundable'}
                              onChange={(e) => setBookingForm({ ...bookingForm, payment_option: e.target.value })}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <div className="font-semibold text-gray-900">Non-Refundable Rate</div>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  Save {nonRefundableDiscount}%
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                Pay {formatCurrency(discountedRoomPrice)} now (was {formatCurrency(totalPriceWithExtra)}).
                                Save {formatCurrency(roomSavings)}! Non-refundable and cannot be modified.
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pay on Arrival - Only show if enabled */}
                      {paymentSettings.enable_pay_on_arrival && (
                        <div 
                          className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          bookingForm.payment_option === 'pay_on_arrival' 
                            ? 'border-gray-600 bg-gray-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setBookingForm({ ...bookingForm, payment_option: 'pay_on_arrival' })}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            name="payment_option"
                            value="pay_on_arrival"
                            checked={bookingForm.payment_option === 'pay_on_arrival'}
                            onChange={(e) => setBookingForm({ ...bookingForm, payment_option: e.target.value })}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">Pay on Arrival</div>
                            <div className="text-sm text-gray-600 mt-1">
                              Reserve now, pay {formatCurrency(finalTotalPrice)} when you check in.
                            </div>
                          </div>
                        </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    {/* Price Breakdown Table */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Price Summary</h4>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-gray-200">
                            <td className="py-2 text-gray-600">Room ({roomType.nights} night{roomType.nights !== 1 ? 's' : ''} × {bookingForm.quantity} room{bookingForm.quantity !== 1 ? 's' : ''})</td>
                            <td className="py-2 text-right font-medium">{formatCurrency(totalPrice)}</td>
                          </tr>
                          {extraPersonFeeTotal > 0 && (
                            <tr className="border-b border-gray-200">
                              <td className="py-2 text-gray-600">
                                3rd person ({extraPersons} {extraPersons === 1 ? 'guest' : 'guests'} × {formatCurrency(extraPersonFeePerNight)}/night × {roomType.nights} night{roomType.nights !== 1 ? 's' : ''})
                              </td>
                              <td className="py-2 text-right font-medium">{formatCurrency(extraPersonFeeTotal)}</td>
                            </tr>
                          )}
                          {bookingForm.payment_option === 'non_refundable' && nonRefundableDiscount > 0 && (
                            <tr className="border-b border-gray-200 text-green-600">
                              <td className="py-2">Non-refundable discount ({nonRefundableDiscount}%)</td>
                              <td className="py-2 text-right font-medium">-{formatCurrency(roomSavings)}</td>
                            </tr>
                          )}
                          <tr className="border-b-2 border-gray-300 font-semibold">
                            <td className="py-2 text-gray-900">Total Amount</td>
                            <td className="py-2 text-right text-lg text-blue-600">{formatCurrency(finalTotalPrice)}</td>
                          </tr>
                          {selectedCar && (
                            <tr className="bg-amber-50 border-b border-amber-200">
                              <td className="py-2 text-amber-800">
                                <span className="font-medium">Car Rental ({selectedCar.car_days} day{selectedCar.car_days !== 1 ? 's' : ''})</span>
                                <br/><span className="text-xs text-amber-600 italic">Payable on arrival</span>
                              </td>
                              <td className="py-2 text-right font-medium text-amber-700">{formatCurrency(selectedCar.car_amount)}</td>
                            </tr>
                          )}
                          {transferAmount > 0 && (
                            <tr className="bg-sky-50 border-b border-sky-200">
                              <td className="py-2 text-sky-800">
                                <span className="font-medium">Airport Transfer</span>
                                <br/><span className="text-xs text-sky-600 italic">Payable directly to driver</span>
                              </td>
                              <td className="py-2 text-right font-medium text-sky-700">{formatCurrency(transferAmount)}</td>
                            </tr>
                          )}
                          {bookingForm.payment_option === 'flexible' && (
                            <>
                              <tr className="bg-blue-50">
                                <td className="py-2 text-blue-800 font-medium">Deposit Payment Now ({flexibleRatePercentage}%)</td>
                                <td className="py-2 text-right font-bold text-blue-600">{formatCurrency(finalTotalPrice * (flexibleRatePercentage / 100))}</td>
                              </tr>
                              <tr className="bg-amber-50">
                                <td className="py-2 text-amber-800 font-medium">Balance Due on Arrival</td>
                                <td className="py-2 text-right font-bold text-amber-600">{formatCurrency(finalTotalPrice * (1 - flexibleRatePercentage / 100))}</td>
                              </tr>
                            </>
                          )}
                          {bookingForm.payment_option === 'non_refundable' && (
                            <tr className="bg-green-50">
                              <td className="py-2 text-green-800 font-medium">Full Payment Now</td>
                              <td className="py-2 text-right font-bold text-green-600">{formatCurrency(finalTotalPrice)}</td>
                            </tr>
                          )}
                          {bookingForm.payment_option === 'pay_on_arrival' && (
                            <tr className="bg-amber-50">
                              <td className="py-2 text-amber-800 font-medium">Pay at Check-in</td>
                              <td className="py-2 text-right font-bold text-amber-600">{formatCurrency(finalTotalPrice)}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      
                      {/* Environmental Fee Notice */}
                      <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-emerald-800">Environmental Fee</p>
                          <p className="text-sm text-emerald-700">
                            €{environmentalFee} per night ({formatCurrency(environmentalFee * roomType.nights * bookingForm.quantity)} total) will be paid at check-in time.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Terms and Conditions Checkbox */}
                  <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="mt-1 mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="terms" className="text-sm text-gray-700 cursor-pointer">
                        I have read and accept the{' '}
                        <button
                          type="button"
                          onClick={() => setShowTermsModal(true)}
                          className="text-blue-600 hover:text-blue-800 font-semibold underline"
                        >
                          Terms and Conditions
                        </button>
                        , including the cancellation and refund policies.
                      </label>
                    </div>
                  </div>

                  {/* Payment Method Selection - Show if multiple gateways enabled */}
                  {(
                    (paymentSettings.enable_stripe && paymentSettings.enable_nexi) ||
                    (paymentSettings.enable_stripe && paymentSettings.enable_paypal) ||
                    (paymentSettings.enable_nexi && paymentSettings.enable_paypal)
                  ) && (
                    <div className="border-t pt-4">
                      <Label className="text-sm font-semibold mb-2 block text-gray-700">Select Payment Method</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {paymentSettings.enable_stripe && (
                          <div 
                            className={`border-2 rounded-lg p-2 cursor-pointer transition-all ${
                              paymentGateway === 'stripe' 
                                ? 'border-blue-600 bg-blue-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => setPaymentGateway('stripe')}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="payment_gateway_checkout"
                                value="stripe"
                                checked={paymentGateway === 'stripe'}
                                onChange={() => setPaymentGateway('stripe')}
                              />
                              <span className="font-medium text-gray-900 text-sm">💳 Card Payment</span>
                            </div>
                          </div>
                        )}

                        {paymentSettings.enable_nexi && (
                          <div 
                            className={`border-2 rounded-lg p-2 cursor-pointer transition-all ${
                              paymentGateway === 'nexi' 
                                ? 'border-orange-600 bg-orange-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => setPaymentGateway('nexi')}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="payment_gateway_checkout"
                                value="nexi"
                                checked={paymentGateway === 'nexi'}
                                onChange={() => setPaymentGateway('nexi')}
                              />
                              <span className="font-medium text-gray-900 text-sm">🟧 Nexi XPay</span>
                            </div>
                          </div>
                        )}

                        {paymentSettings.enable_paypal && (
                          <div 
                            className={`border-2 rounded-lg p-2 cursor-pointer transition-all ${
                              paymentGateway === 'paypal' 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => setPaymentGateway('paypal')}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="payment_gateway_checkout"
                                value="paypal"
                                checked={paymentGateway === 'paypal'}
                                onChange={() => setPaymentGateway('paypal')}
                              />
                              <span className="font-medium text-gray-900 text-sm">🅿️ PayPal</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-lg py-6 font-bold shadow-lg"
                    disabled={loading || !termsAccepted}
                  >
                    {loading ? (loadingMessage || 'Processing...') : `Complete Booking - ${formatCurrency(finalTotalPrice)}`}
                  </Button>

                  {!termsAccepted && (
                    <p className="text-sm text-red-600 text-center">
                      Please accept the Terms and Conditions to continue
                    </p>
                  )}
                </form>
              </CardContent>
              
              {/* Security Footer Strip */}
              <div className="border-t bg-gray-50 px-4 py-3">
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs font-medium">SSL Secure</span>
                  </div>
                  <div className="h-4 w-px bg-gray-300"></div>
                  <div className="flex items-center gap-2">
                    <PaymentMethodLogos size="sm" />
                  </div>
                  <div className="h-4 w-px bg-gray-300"></div>
                  <button 
                    type="button"
                    onClick={() => setShowSecurityModal(true)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline"
                  >
                    Learn more
                  </button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
      </main>

      {/* Footer */}
      <Footer />

      {/* Security Info Modal */}
      {showSecurityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Transaction Security</h3>
                    <p className="text-sm text-gray-600">Your payment is protected</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSecurityModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4 text-sm text-gray-700">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold text-green-800">Secure Payment Processing</p>
                      <p className="text-green-700 text-xs mt-1">Powered by Nexi XPay with bank-level encryption</p>
                    </div>
                  </div>
                </div>

                <p>
                  All payments made using credit or debit cards are processed through the electronic payment platform 
                  <span className="font-semibold text-blue-900"> Nexi XPay</span> of{' '}
                  <span className="font-semibold">Nexi Payments Greece S.A.</span>
                </p>
                
                <p>
                  The platform uses <span className="font-semibold">TLS 1.2 encryption protocol</span> with{' '}
                  <span className="font-semibold">128-bit Secure Sockets Layer (SSL)</span> encryption.
                </p>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold text-blue-900 mb-1">What is encryption?</p>
                      <p className="text-xs text-blue-800">
                        Encryption is a way of coding your information until it reaches its recipient, 
                        who will be able to decode it using the appropriate key. This ensures your 
                        payment details remain completely secure throughout the transaction.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-xs text-gray-500 mb-3">Accepted Payment Methods:</p>
                  <div className="flex items-center justify-center gap-4 flex-wrap">
                    <PaymentMethodLogos size="lg" />
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowSecurityModal(false)}
                className="w-full mt-6 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => {
          setTermsAccepted(true);
          setShowTermsModal(false);
        }}
      />
    </div>
  );
}

export default RoomTypeDetails;
